import { useEffect, useState } from 'react';

import { ErroDeRede, carregarCatalogo } from '../nucleo/api.js';
import { NOME_DA_ESPECIE, agruparPorEspecie } from '../nucleo/especies.js';
import { enxuto, expressivo, numero, plural } from '../nucleo/numero.js';
import { forcaDaProcedencia } from '../nucleo/confianca.js';
import { SeloDaProcedencia } from '../componentes/SeloDeConfianca.jsx';
import { paraRota } from '../nucleo/rota.js';

/**
 * A segunda tela: o que o app sabe sobre cada material, e o que não sabe.
 *
 * O simulador responde "quanto dá". Esta tela responde a pergunta que vem
 * depois, e que é a que decide se o estudo passa numa análise de crédito: **de
 * onde saiu esse número**. São os dois públicos no mesmo lugar, em
 * profundidades diferentes — o produtor lê quanto gás sai de uma tonelada, o
 * consultor lê o BMP, a faixa e quantos ensaios a sustentam.
 *
 * Três decisões de produto que o layout obedece:
 *
 * **As cinco categorias sem análise ficam na lista, não num rodapé.** Elas
 * somam quase metade do catálogo. Separá-las num "em breve" deixaria a lista
 * mais vendável e mentiria por omissão sobre o tamanho da lacuna — que é do
 * setor, não deste app. Cada uma traz o texto do que exatamente falta, porque
 * "sem análise" sem o motivo é só uma desculpa.
 *
 * **A ordem dentro de cada espécie é por procedência, não alfabética.** Quem
 * abre esta tela lê de cima para baixo e para quando acha o que serve; a
 * ordem alfabética colocaria uma estimativa antes de uma referência publicada
 * e ensinaria a ler na ordem errada.
 *
 * **O número grande é o do produtor, não o do laboratório.** O BMP em
 * NmL CH4/g SV é a medida que existe, mas ninguém compra dejeto por grama de
 * sólido volátil: o que se bombeia é tonelada. O BMP aparece logo abaixo, com
 * faixa e número de ensaios, para quem precisa dele.
 *
 * Esta tela usa o catálogo **em cache** (`carregarCatalogo`) e não a rota de
 * detalhe por chave. Medido em 15/10/2026: `/v1/substratos/{chave}` devolve
 * exatamente o mesmo objeto que já vem na lista — mesmos 27 campos, mesmas
 * séries de estocagem, nenhum valor diferente. Uma segunda chamada seria uma
 * ida à rede por dado que já está no aparelho, e pior: `carregarSubstrato`
 * não tem cache, então o detalhe deixaria de abrir offline. A promessa de
 * funcionar sem sinal vale mais que a simetria com a API.
 */

/** O que o cartão mostra em destaque, e o que fazer quando não existe. */
function Rendimento({ item }) {
  if (item.rendimento_nm3_t == null) {
    return (
      <p className="substrato__ausente">
        Sem rendimento por tonelada: {item.rendimento_faixa_ausente_porque || 'falta análise deste material'}.
      </p>
    );
  }

  const faixa = item.rendimento_faixa_nm3_t;
  return (
    <p className="substrato__destaque">
      <strong className="numero-grande">{enxuto(item.rendimento_nm3_t, 2)}</strong>{' '}
      <span className="unidade">Nm³ CH₄ por tonelada</span>
      {faixa ? (
        <span className="substrato__faixa">
          {' '}
          conforme o material: de {enxuto(faixa[0], 2)} a {enxuto(faixa[1], 2)}
        </span>
      ) : null}
    </p>
  );
}

/** A leitura técnica — a que o consultor copia para o relatório. */
function Analise({ item, rotuloDoAnalogo }) {
  const linhas = [];
  // Ver `NumerosEmprestados`: quando o número é do vizinho, a contagem de
  // ensaios também é, e uma contagem sem dono é a parte que mais engana.
  const emprestado = Boolean(item.analogo) && item.bmp != null;
  const deQuem = rotuloDoAnalogo || item.analogo;

  if (item.bmp != null) {
    const faixa = item.bmp_faixa;
    linhas.push([
      'Potencial de metano (BMP)',
      `${expressivo(item.bmp)} NmL CH₄/g SV`,
      faixa ? `faixa publicada ${enxuto(faixa[0], 2)}–${enxuto(faixa[1], 2)}` : 'sem faixa publicada',
    ]);
  }
  if (item.bmp_n != null) {
    linhas.push([
      'Ensaios que sustentam',
      plural(item.bmp_n, 'ensaio independente', 'ensaios independentes'),
      emprestado ? `medidos em ${deQuem}, não nesta categoria` : '',
    ]);
  }
  if (item.st_pct != null) {
    linhas.push(['Sólidos totais (ST)', `${numero(item.st_pct, 2)} %`, 'da massa úmida']);
  }
  if (item.sv_pct_do_st != null) {
    linhas.push(['Sólidos voláteis (SV)', `${numero(item.sv_pct_do_st, 1)} %`, 'do ST']);
  }
  if (item.sv_pct_umido != null) {
    linhas.push(['SV na massa úmida', `${numero(item.sv_pct_umido, 2)} %`, '']);
  }
  if (item.nm3_ch4_por_cabeca_dia != null) {
    linhas.push([
      'Por cabeça, por dia',
      `${enxuto(item.nm3_ch4_por_cabeca_dia, 4)} Nm³ CH₄`,
      item.dejeto_kg_cab_dia != null ? `de ${enxuto(item.dejeto_kg_cab_dia, 1)} kg de dejeto` : '',
    ]);
  }

  if (!linhas.length) return null;

  return (
    <dl className="ficha">
      {linhas.map(([rotulo, valor, nota]) => (
        <div className="ficha__linha" key={rotulo}>
          <dt>{rotulo}</dt>
          <dd>
            {valor}
            {nota ? <span className="terciario"> {nota}</span> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * O que falta nesta categoria, dito pelo que falta — e com a saída.
 *
 * Apontar a lacuna sem apontar o caminho é só dizer não. Quando existe um
 * análogo, ele é dito como análogo e nunca como substituto: a tela do
 * simulador recusa a conta, e dizer aqui "use o de terminação" ensinaria
 * exatamente o erro que a recusa evita.
 */
function Pendencia({ item, rotuloDoAnalogo }) {
  if (!item.pendencia && !item.analogo) return null;

  return (
    <div className="pendencia">
      {item.pendencia ? <p>{item.pendencia}</p> : null}
      {item.analogo ? (
        <p className="terciario">
          Material mais parecido no catálogo: {rotuloDoAnalogo || item.analogo}. Serve de ordem de
          grandeza para conversar, não de linha de estudo — a conta com esta categoria continua
          recusada até haver ensaio.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Quando os números do cartão foram medidos em OUTRO material.
 *
 * Defeito que o navegador mostrou em 15/10/2026 e que 240 testes de jsdom não
 * pegaram: o cartão de `suino_crescimento` imprimia "Ensaios que sustentam: 3
 * ensaios independentes" e, duas linhas abaixo, "a conta com esta categoria
 * continua recusada até haver ensaio". Os três ensaios são da terminação — 15
 * dos 27 campos da entrada são idênticos byte a byte aos dela. O motor sempre
 * declarou o empréstimo na `observacao`; a lista é que não imprimia a
 * ressalva, e duas frases que se contradizem na mesma tela ensinam a não ler
 * nenhuma das duas.
 *
 * Vem ANTES dos números, de propósito. Ressalva depois do número chega tarde:
 * quem lê de cima para baixo já anotou o 303.
 *
 * O gatilho não é "tem análogo". Das cinco entradas com análogo, quatro não
 * publicam número nenhum, e para elas o parágrafo do análogo já é honesto
 * sozinho. O problema é publicar o número do vizinho sem dizer de quem é.
 */
function NumerosEmprestados({ item, rotuloDoAnalogo }) {
  if (!item.analogo || item.bmp == null) return null;

  return (
    <p className="terciario substrato__emprestado">
      Atenção: os números abaixo não foram medidos neste material. São os de{' '}
      {rotuloDoAnalogo || item.analogo}, aplicados aqui por analogia porque não existe ensaio
      separado desta categoria.
    </p>
  );
}

function Cartao({ item, rotuloDoAnalogo }) {
  const temAnalise = item.bmp != null;

  return (
    <article className={`cartao substrato ${temAnalise ? '' : 'substrato--sem-analise'}`}>
      <header className="substrato__cabeca">
        <h3 className="substrato__titulo">
          <a href={paraRota('substratos', item.chave)}>{item.rotulo}</a>
        </h3>
        <SeloDaProcedencia valor={item.confianca} />
      </header>

      <NumerosEmprestados item={item} rotuloDoAnalogo={rotuloDoAnalogo} />
      <Rendimento item={item} />
      <Analise item={item} rotuloDoAnalogo={rotuloDoAnalogo} />
      <Pendencia item={item} rotuloDoAnalogo={rotuloDoAnalogo} />

      {item.fibroso ? (
        <p className="terciario substrato__fibroso">
          Material fibroso: pede mais tempo dentro do biodigestor que chorume de suíno.
          {/* A segunda frase falava do "número acima" em três cartões que não
              imprimem número nenhum — a linha de cima deles diz "Sem
              rendimento por tonelada". Apontar para um número que não está
              lá faz o leitor procurar o próprio erro. */}
          {item.rendimento_nm3_t != null
            ? ' O número acima é o potencial do material, não o que um reator de retenção curta entrega.'
            : item.bmp != null
              ? ' O potencial publicado é o do material, não o que um reator de retenção curta entrega.'
              : ''}
        </p>
      ) : null}

      <p className="substrato__rodape">
        <a href={paraRota('substratos', item.chave)}>Ver a fonte e as séries de estocagem →</a>
      </p>
    </article>
  );
}

function Carregando() {
  return (
    <p className="secundario" role="status">
      Carregando o catálogo…
    </p>
  );
}

function SemCatalogo({ erro }) {
  const offline = erro instanceof ErroDeRede && erro.offline;
  return (
    <section className="cartao" role="alert">
      <h2 className="recusa__titulo">
        {offline ? 'Sem conexão e sem cópia guardada' : 'Não consegui carregar o catálogo'}
      </h2>
      <p>
        {offline
          ? 'Abra o app uma vez com internet: depois disso o catálogo fica guardado no aparelho e esta tela funciona sem sinal.'
          : erro?.message}
      </p>
    </section>
  );
}

/** Ordena por procedência e, dentro dela, pelo maior potencial. */
function ordenar(itens) {
  return [...itens].sort((a, b) => {
    const f = forcaDaProcedencia(a.confianca) - forcaDaProcedencia(b.confianca);
    if (f !== 0) return f;
    return (b.bmp ?? -1) - (a.bmp ?? -1);
  });
}

export function Substratos() {
  const [catalogo, setCatalogo] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    let vivo = true;
    carregarCatalogo()
      .then((c) => {
        if (vivo) setCatalogo(c);
      })
      .catch((e) => {
        if (vivo) setErro(e);
      });
    return () => {
      vivo = false;
    };
  }, []);

  if (erro) return <SemCatalogo erro={erro} />;
  if (!catalogo) return <Carregando />;

  const itens = catalogo.itens ?? [];
  const rotulos = new Map(itens.map((i) => [i.chave, i.rotulo]));
  const grupos = agruparPorEspecie(itens);

  return (
    <div className="empilha-largo">
      <section className="bloco">
        <h2 className="simulador__titulo">O que já foi medido</h2>
        <p className="secundario">
          {plural(catalogo.total, 'categoria de dejeto', 'categorias de dejeto')} no catálogo:{' '}
          {catalogo.com_analise} com análise de laboratório e {catalogo.sem_analise} ainda
          sem. As sem análise estão aqui no meio das outras, com o motivo — esconder o que falta
          deixaria a lista mais bonita e não mudaria o fato.
        </p>
        {catalogo.deCache ? (
          <p className="ressalva">
            Esta é a cópia guardada no aparelho. Pode estar velha; abra com internet para conferir.
          </p>
        ) : null}
      </section>

      {grupos.map(([especie, doGrupo]) => (
        <section className="bloco" key={especie}>
          <h3 className="rotulo-secao">{NOME_DA_ESPECIE[especie] ?? especie}</h3>
          <div className="empilha">
            {ordenar(doGrupo).map((item) => (
              <Cartao
                key={item.chave}
                item={item}
                rotuloDoAnalogo={item.analogo ? rotulos.get(item.analogo) : ''}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
